/**
 * Helpable-at-event reader (Track 2 Phase A) - the ONE base hook behind all
 * three helpable surfaces (the "For you" section, the live-pill count chip, and
 * the feed chips). It fetches the matcher's already-judged matches where the
 * viewer is the helper, intersected with the event's post set, from a single
 * viewer-scoped endpoint (spec P5: section cards + pill count from one fetch).
 * W5's feed chips MUST match against these same `items` - no second fetch.
 *
 * All-events (2026-07-13 amendment): the endpoint serves any viewable,
 * non-deleted event (ACTIVE or ARCHIVED, any phase - feed parity with
 * get_event_feed; only DELETED / not-visible 404) - there is no live-window
 * gate. The section and feed chips ask on every phase; only the live-only
 * surfaces (the pill chip, Meetverse) still pass their own `enabled` gate.
 *
 * The query self-disables (no network) when ANY of these hold, so callers never
 * fetch off-surface:
 *   - the viewer is off-surface (`useHelpableAtEventEnabled` is false: the flag
 *     is off AND the viewer is not a Meetball admin - admins are always on for
 *     dogfooding, the flag governs only the public flip; kill switch, spec 3.4),
 *   - there is no `eventId`,
 *   - the caller passes `enabled: false` - a caller-owned gate for surfaces that
 *     exist only during live (the pill chip, Meetverse); the section and feed
 *     chips omit it.
 *
 * Freshness matches the live rail: `staleTime` = `railStaleTimeMs` (60s) and
 * refetch on window focus, so a returning goer sees a current answer (spec 2.3).
 *
 * `enabled` in the RESULT is the server's field, NOT the input option: `false`
 * means kill (the backend lever is off) or a fetch error, and the surfaces
 * render NOTHING - distinct from `enabled: true` with empty `items` (the
 * invitation state). On the disabled/loading/error paths the result collapses to
 * `{ items: [], total: 0, enabled: false }`, which every surface reads as
 * "render nothing / stay collapsed" (spec 3.4 - fetch errors are silent).
 *
 * A DISABLED observer collapses on the INPUT gate, before reading `data`: the
 * section fetches unconditionally and warms `eventHelpableKey(event.id)`, and
 * the goers-only / live-only surfaces (pill chip, Meetverse, LiveSpaceSlot) pass
 * `enabled: false` while sharing that SAME key. React Query hands a disabled
 * observer the warm cache, so returning `data` here would leak the section's
 * matches into surfaces meant to stay dark. Collapsing on `queryEnabled` also
 * covers the no-eventId callers, so the shared empty-id key can never surface.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getEventHelpable } from '@api-client/api-client';
import { LIVE_EVENTS_CONFIG } from '@config/liveEvents';
import { useHelpableAtEventEnabled } from '@hooks/useHelpableAtEventEnabled';
import { eventHelpableKey } from '@lib/cache/events';
import type { HelpableAtEventItem } from '@models/api';

export interface UseEventHelpableOptions {
  /**
   * Caller gate on top of the flag/eventId checks - pass `false` to keep the
   * query idle. Used only by live-only surfaces (the pill chip, Meetverse) to
   * stand down outside the live window; the section and feed chips ask on every
   * phase and omit it. Defaults to `true`.
   */
  enabled?: boolean;
}

export interface UseEventHelpableResult {
  /** The capped helpable posts for this viewer at this event (empty off-surface). */
  items: HelpableAtEventItem[];
  /** Full match count BEFORE the server cap - drives the "+N more" footer. */
  total: number;
  /** Server `enabled`: `false` = kill (render nothing). See the hook header. */
  enabled: boolean;
  /** First-load flag (never true while the query is disabled). */
  isLoading: boolean;
}

export function useEventHelpable(
  eventId: string | undefined,
  { enabled = true }: UseEventHelpableOptions = {},
): UseEventHelpableResult {
  const helpableEnabled = useHelpableAtEventEnabled();
  const queryEnabled = helpableEnabled && !!eventId && enabled;

  const { data, isLoading } = useQuery({
    queryKey: eventHelpableKey(eventId ?? ''),
    queryFn: async () => {
      if (!eventId) throw new Error('eventId is required');
      const response = await getEventHelpable(eventId);
      return response.data;
    },
    enabled: queryEnabled,
    staleTime: LIVE_EVENTS_CONFIG.railStaleTimeMs,
    refetchOnWindowFocus: true,
  });

  return useMemo<UseEventHelpableResult>(() => {
    // Disabled observers never read the shared warm cache (see the hook header):
    // collapse to the off-surface shape so a goers-only / live-only caller with
    // its gate closed stays dark even when the section already warmed the key.
    // `isLoading` is false for a disabled query, so the collapse is honest.
    if (!queryEnabled) {
      return { items: [], total: 0, enabled: false, isLoading: false };
    }
    return {
      items: data?.items ?? [],
      total: data?.total ?? 0,
      enabled: data?.enabled ?? false,
      isLoading,
    };
  }, [queryEnabled, data, isLoading]);
}
